import { format } from 'date-fns';
import { rrulestr } from 'rrule';
import { compact } from '.';
import {
  noteContentAndShiftChangesToString,
  noteDateEqualsShiftDate,
  noteToString,
} from '../../components/Notes/util';
import type {
  DerivedRosterShift,
  DerivedTeamRoster,
  StoredShiftEvent,
} from '../../hooks/types';
import { reportDateTimeFormat } from './date-time';

/**
 * The column headers for roster csv report.
 * @param includeUserPhoneNumberColumn - whether to include the user phone number column.
 * @param includeUserColumns - whether to include the user columns.
 * @returns csv columns
 */
export const rosterCsvColumns = (
  includeUserPhoneNumberColumn: boolean,
  includeUserColumns: boolean
): readonly string[] =>
  compact([
    'Team Name',
    'Team Type',
    'IO Number',
    'Role Name',
    'Role Group',
    includeUserColumns ? 'Username' : undefined,
    includeUserColumns ? 'Email' : undefined,
    includeUserColumns && includeUserPhoneNumberColumn
      ? 'Phone Number'
      : undefined,
    includeUserColumns ? 'First Name' : undefined,
    includeUserColumns ? 'Last Name' : undefined,
    includeUserColumns ? 'Employment Type' : undefined,
    'Created At',
    'Created By',
    'Local Start Time',
    'Local End Time',
    'ISO Start Time',
    'ISO End Time',
    'Status',
    'Notes',
  ]);

/**
 * Curried function that returns csv rows.
 *
 * NOTE: The order of each row should match the order of the columns in {@link rosterCsvColumns}.
 *
 * @param shifts - shifts to create csv rows for
 * @param teamAndRoleData - team and role data to include in the csv row
 * @returns a function that takes user columns and returns csv rows
 */
const createCsvRows =
  (shifts: readonly DerivedRosterShift[], teamAndRoleData: readonly string[]) =>
  /**
   * Create csv rows.
   * @param userData - user data to include in the csv row
   * @returns csv rows
   */
  (userData?: readonly string[]): readonly (readonly string[])[] => {
    return shifts.map((shift) => {
      const shiftData: readonly string[] = [
        format(new Date(shift.createdAt), reportDateTimeFormat),
        shift.createdBy,
        format(new Date(shift.startTime), reportDateTimeFormat),
        format(new Date(shift.endTime), reportDateTimeFormat),
        shift.startTime,
        shift.endTime,
        shift.status,
        shift.relatedNotes
          .filter(
            ({ date }) =>
              date !== undefined &&
              noteDateEqualsShiftDate(date, shift.startTime)
          )
          .map(noteToString)
          .join(' | '),
      ];

      return userData === undefined
        ? [...teamAndRoleData, ...shiftData]
        : [...teamAndRoleData, ...userData, ...shiftData];
    });
  };

/**
 * Converts team rosters to csv data.
 * @param teamRosters - team rosters to convert to csv data.
 * @param showUserPhoneNumber - whether to include the user phone number.
 * @param shiftFilter - whether to include only vacant shits.
 * @returns csv data.
 */
export const rostersToCsvData = (
  teamRosters: readonly DerivedTeamRoster[],
  showUserPhoneNumber: boolean,
  shiftFilter: 'only vacant shifts' | 'only non-vacant shifts' | 'all shifts'
): readonly (readonly string[])[] => {
  const filterOnlyVacantShifts = shiftFilter === 'only vacant shifts';
  const columns = rosterCsvColumns(
    showUserPhoneNumber,
    !filterOnlyVacantShifts
  );
  const rows: readonly (readonly string[])[] = teamRosters.flatMap(
    (teamRoster) =>
      teamRoster.roleRosters
        .flatMap((roleRoster) => {
          const teamAndRoleData: readonly string[] = [
            teamRoster.teamName,
            teamRoster.teamType,
            teamRoster.internalOrderNumber ?? '',
            roleRoster.roleName,
            roleRoster.roleGroup,
          ];

          const createRowsForVacantShiftsFunc = createCsvRows(
            roleRoster.vacantShifts,
            teamAndRoleData
          );

          if (filterOnlyVacantShifts) {
            return createRowsForVacantShiftsFunc();
          }

          const nonVacantShiftRows: readonly (readonly string[])[] =
            roleRoster.users.flatMap((user) =>
              createCsvRows(
                user.shifts,
                teamAndRoleData
              )(
                compact([
                  user.username,
                  user.email ?? '',
                  showUserPhoneNumber ? user.phoneNumber ?? '' : undefined,
                  user.firstName,
                  user.lastName,
                  user.userType ?? '',
                ])
              )
            );

          if (shiftFilter === 'only non-vacant shifts') {
            return nonVacantShiftRows;
          }

          return [
            ...createRowsForVacantShiftsFunc(
              // Since phone number data is not included, we create 1 less cell
              (showUserPhoneNumber ? Array(6) : Array(5)).slice().fill('')
            ),
            ...nonVacantShiftRows,
          ];
        })
        // Sorts shifts within teams by their iso start time.
        .toSorted((a, b) => {
          const startTimeIndex = columns.indexOf('ISO Start Time');

          return (
            new Date(a[startTimeIndex] ?? '').getTime() -
            new Date(b[startTimeIndex] ?? '').getTime()
          );
        })
  );
  return [columns, ...rows];
};

/**
 * The column headers for shift event csv report.
 */
export const shiftEventCsvColumns: readonly string[] = [
  'Shift Id',
  'Shift Event',
  'Shift Event Created At',
  'Shift Event Created By',
  'Shift Local Start Time',
  'Shift Local End Time',
  'Shift ISO Start Time',
  'Shift ISO End Time',
  'Shift Status',
  'Shift Notes',
  'Is Recurring Shift',
  'Shift Recurrence Pattern',
  'Shift Recurrence End Date',
  'Username',
  'Email',
  'First Name',
  'Last Name',
  'Employment Type',
  'Role Name',
  'Role Group',
  'Team Name',
  'Team Type',
  'Internal Order Number',
];

/**
 * Parse IMT Data (shift events) to csv rows.
 * @param currentShiftEvent target IMT Data.
 * @returns csv rows.
 */
export const shiftEventToCsvRows = (
  currentShiftEvent: StoredShiftEvent
): readonly string[] => {
  const createdAt = format(
    new Date(currentShiftEvent.eventCreatedAt),
    reportDateTimeFormat
  );

  const shiftStartTimeLocal = format(
    new Date(currentShiftEvent.shiftStartTime),
    reportDateTimeFormat
  );
  const shiftEndTimeLocal = format(
    new Date(currentShiftEvent.shiftEndTime),
    reportDateTimeFormat
  );

  const tmpShiftRecurrencePattern =
    currentShiftEvent.shiftRecurrencePattern ?? '';
  const shiftRecurrencePattern = currentShiftEvent.shiftIsRecurring
    ? rrulestr(tmpShiftRecurrencePattern).toText()
    : 'Not recurring shift';

  const shiftRecurrenceEndDate = currentShiftEvent.shiftIsRecurring
    ? format(
        new Date(currentShiftEvent.shiftRecurrenceEndDate),
        reportDateTimeFormat
      )
    : '';

  const shiftNoteContent = currentShiftEvent.shiftNotes;

  const shiftNote =
    shiftNoteContent !== undefined
      ? noteContentAndShiftChangesToString(
          shiftNoteContent.metadata?.shift?.changes ?? [],
          shiftNoteContent.content
        )
      : '';

  return [
    currentShiftEvent.shiftId,
    currentShiftEvent.shiftEvent,
    createdAt,
    currentShiftEvent.eventCreatedBy,
    shiftStartTimeLocal,
    shiftEndTimeLocal,
    currentShiftEvent.shiftStartTime,
    currentShiftEvent.shiftEndTime,
    currentShiftEvent.shiftStatus,
    shiftNote,
    currentShiftEvent.shiftIsRecurring ? 'Yes' : 'No',
    shiftRecurrencePattern,
    shiftRecurrenceEndDate,
    currentShiftEvent.username ?? '',
    currentShiftEvent.userEmail ?? '',
    currentShiftEvent.userFirstName ?? '',
    currentShiftEvent.userLastName ?? '',
    currentShiftEvent.userEmploymentType ?? '',
    currentShiftEvent.roleName,
    currentShiftEvent.roleGroup ?? '',
    currentShiftEvent.teamName,
    currentShiftEvent.teamType,
    currentShiftEvent.teamIon ?? '',
  ];
};
